import { useMemo } from 'react';

import {
  type Precision,
  formatDateTimeShort,
  formatHumanReadableDate,
  formatIso8601PlainDate,
  formatUsPlainDateShort,
  isValidDate,
} from '../utils/time';

/**
 * Time component for displaying dates and times
 *
 * @example
 * Display date only
 * <Time format="date" when="2024-01-01T12:34:56.789Z" />
 * Output: <time dateTime="2024-01-01">1/1/24</time>
 *
 * Display time with seconds and milliseconds
 * <Time format="time" when="2024-01-01T12:34:56.789Z" precision="milliseconds" />
 * Output: <time dateTime="12:34:56.789">12:34:56.789</time>
 *
 */
interface TimeProps {
  when: string | Date;
  timeZone?: string;
  format?: 'date' | 'time' | 'datetime' | 'human-readable-date';
  precision?: Precision;
}

export function Time(props: TimeProps) {
  const { when, format, timeZone, precision } = props;

  const { dateStr, dateTime } = useMemo(() => {
    const date = new Date(when);
    if (!isValidDate(date)) {
      return {
        dateStr: when.toString(),
        dateTime: undefined,
      };
    }

    if (format === 'date') {
      return {
        dateStr: formatUsPlainDateShort(date, { timeZone }),
        dateTime: formatIso8601PlainDate(date, {
          timeZone,
        }),
      };
    } else if (format === 'human-readable-date') {
      return {
        dateStr: formatHumanReadableDate(date, { timeZone }),
        dateTime: formatIso8601PlainDate(date, {
          timeZone,
        }),
      };
    } else if (format === 'time') {
      return {
        dateStr: date.toLocaleTimeString(undefined, {
          hour: 'numeric',
          minute: 'numeric',
          second: precision === 'seconds' || precision === 'milliseconds' ? '2-digit' : undefined,
          fractionalSecondDigits: precision === 'milliseconds' ? 3 : undefined,
        }),
        dateTime: date.toISOString().split('T')[1],
      };
    } else {
      // datetime is the default
      return {
        dateStr: formatDateTimeShort(date, {
          precision,
          timeZone,
        }),
        dateTime: date.toISOString(),
      };
    }
  }, [when, format, timeZone, precision]);

  return <time dateTime={dateTime}>{dateStr}</time>;
}
